import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';

/**
 * SpiralCred Information Page
 * Explains the trust-based credit system
 */
const SpiralCredInfo = ({ onBackToDirectory, onNavigateToOnboarding }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);

  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.15) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      
      {/* Ultra subtle Grant spiral */}
      <div className="fixed inset-0 pointer-events-none opacity-5" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) brightness(1.3) saturate(1.5)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      <style>{`
        .glass-block {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03));
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.5);
        }
      `}</style>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        
        {/* Back Button */}
        <button 
          onClick={onBackToDirectory}
          className="mb-8 flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
          style={{ fontFamily: "'Cinzel', serif" }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Directory
        </button>
        
        <h1 
          className="text-5xl md:text-6xl font-bold text-center mb-16"
          style={{
            fontFamily: "'Cinzel', serif",
            letterSpacing: '0.15em',
            color: '#E6C794',
            textShadow: '0 0 30px rgba(230, 198, 133, 0.4)'
          }}
        >
          SpiralCred
        </h1>

        <div className="space-y-8">
          <div className="glass-block rounded-3xl p-10">
            <p className="text-2xl md:text-3xl font-light text-center mb-6" style={{ fontFamily: "'Cinzel', serif" }}>
              A Secret Private Ledger
            </p>
            <p className="text-lg leading-relaxed text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              SpiralCred allows lawful, equal-value trade between individuals. Everything is by the individual—which makes it lawful. It's not an exchange; it's a way of accounting and keeping track between families on their own accord.
            </p>
          </div>

          <div className="glass-block rounded-3xl p-10">
            <h3 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              How It Works
            </h3>
            <p className="text-lg leading-relaxed text-white/80 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Members trade goods, time, and skills directly. Each exchange is recorded as trust earned and trust given. No interest, no fees, no outside interference—only reciprocity between families.
            </p>
            <p className="text-lg leading-relaxed text-white/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              It's lawful, private, and regenerative—an economy built on relationship instead of extraction.
            </p>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={onNavigateToOnboarding}
              className="glass-block px-12 py-5 hover:bg-white/15 border-2 border-amber-500/40 hover:border-amber-500/70 text-white rounded-2xl text-xl transition-all hover:scale-105"
              style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
            >
              Join SpiralOrg →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiralCredInfo;
